<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-history</v-icon>
        Audit-Log
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Suchen..."
          single-line
          hide-details
          density="compact"
          style="max-width: 300px"
        ></v-text-field>
      </v-card-title>

      <v-card-text>
        <!-- Filter -->
        <v-row dense class="mb-4">
          <v-col cols="12" md="4">
            <v-select
              v-model="filterList"
              :items="availableLists"
              item-title="name"
              item-value="id"
              label="Nach Liste filtern"
              clearable
              density="compact"
              @update:model-value="loadLogs"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filterAction"
              :items="actionTypes"
              label="Nach Aktion filtern"
              clearable
              density="compact"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="itemsPerPage"
              :items="[25, 50, 100, 200]"
              label="Einträge pro Seite"
              density="compact"
              @update:model-value="loadLogs"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Timeline -->
        <v-timeline side="end" density="compact">
          <v-timeline-item
            v-for="log in filteredLogs"
            :key="log.id"
            :dot-color="getActionColor(log.action)"
            size="small"
          >
            <template v-slot:icon>
              <v-icon size="small">{{ getActionIcon(log.action) }}</v-icon>
            </template>

            <v-card class="elevation-2 mb-2">
              <v-card-title class="text-subtitle-1 py-2">
                <v-chip
                  :color="getActionColor(log.action)"
                  size="small"
                  class="mr-2"
                >
                  {{ getActionLabel(log.action) }}
                </v-chip>
                {{ log.description }}
              </v-card-title>

              <v-card-subtitle class="py-1">
                <v-icon size="small" class="mr-1">mdi-account</v-icon>
                {{ log.user_name }}
                <span class="mx-2">•</span>
                <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                {{ log.created_at_formatted }}
              </v-card-subtitle>

              <!-- Details (Old/New Values) -->
              <v-card-text v-if="log.old_value || log.new_value" class="pt-2">
                <v-row dense>
                  <v-col v-if="log.old_value" cols="12" md="6">
                    <div class="text-caption text-grey mb-1">Vorher:</div>
                    <pre class="value-box old-value">{{ formatValue(log.old_value) }}</pre>
                  </v-col>
                  <v-col v-if="log.new_value" cols="12" md="6">
                    <div class="text-caption text-grey mb-1">Nachher:</div>
                    <pre class="value-box new-value">{{ formatValue(log.new_value) }}</pre>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <!-- Pagination -->
        <div class="text-center mt-4">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            @update:model-value="loadLogs"
            total-visible="7"
          ></v-pagination>
          <div class="text-caption text-grey mt-2">
            Zeige {{ logs.length }} von {{ totalCount }} Einträgen
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && logs.length === 0" class="text-center py-8 text-grey">
          <v-icon size="64">mdi-history</v-icon>
          <p class="mt-2">Keine Audit-Log-Einträge gefunden</p>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

// State
const logs = ref([]);
const loading = ref(false);
const search = ref('');
const filterList = ref(null);
const filterAction = ref(null);
const availableLists = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(50);
const totalCount = ref(0);

const actionTypes = [
  { title: 'Liste erstellt', value: 'list_created' },
  { title: 'Liste aktualisiert', value: 'list_updated' },
  { title: 'Liste gelöscht', value: 'list_deleted' },
  { title: 'Spalte hinzugefügt', value: 'column_added' },
  { title: 'Spalte aktualisiert', value: 'column_updated' },
  { title: 'Spalte gelöscht', value: 'column_deleted' },
  { title: 'Daten erstellt', value: 'data_created' },
  { title: 'Daten aktualisiert', value: 'data_updated' },
  { title: 'Daten gelöscht', value: 'data_deleted' },
  { title: 'Berechtigung geändert', value: 'permission_changed' },
  { title: 'Exportiert', value: 'exported' }
];

// Computed
const filteredLogs = computed(() => {
  let filtered = logs.value;

  if (search.value) {
    const searchLower = search.value.toLowerCase();
    filtered = filtered.filter(log =>
      log.description.toLowerCase().includes(searchLower) ||
      log.user_name.toLowerCase().includes(searchLower) ||
      log.list_name.toLowerCase().includes(searchLower)
    );
  }

  if (filterAction.value) {
    filtered = filtered.filter(log => log.action === filterAction.value);
  }

  return filtered;
});

const totalPages = computed(() => {
  return Math.ceil(totalCount.value / itemsPerPage.value);
});

// Methods
const loadLogs = async () => {
  loading.value = true;
  try {
    const params = new URLSearchParams({
      limit: itemsPerPage.value,
      offset: (currentPage.value - 1) * itemsPerPage.value
    });

    if (filterList.value) {
      params.append('list_id', filterList.value);
    }
    params.append('action', 'get_logs');

    const response = await axios.get(`${API_BASE_URL}list_audit.php?${params}`, {
      withCredentials: true
    });

    logs.value = response.data.logs.map(log => ({
      ...log,
      showDetails: false
    }));

    totalCount.value = response.data.pagination.total;
  } catch (error) {
    console.error('Fehler beim Laden der Audit-Logs:', error);
  } finally {
    loading.value = false;
  }
};

const loadAvailableLists = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}dynamic_lists.php?action=all`, {
      withCredentials: true
    });
    availableLists.value = response.data.lists;
  } catch (error) {
    console.error('Fehler beim Laden der Listen:', error);
  }
};

const getActionColor = (action) => {
  const colors = {
    list_created: 'success',
    list_updated: 'info',
    list_deleted: 'error',
    column_added: 'success',
    column_updated: 'warning',
    column_deleted: 'error',
    data_created: 'success',
    data_updated: 'primary',
    data_deleted: 'error',
    permission_changed: 'warning',
    exported: 'info'
  };
  return colors[action] || 'grey';
};

const getActionIcon = (action) => {
  const icons = {
    list_created: 'mdi-plus-circle',
    list_updated: 'mdi-pencil',
    list_deleted: 'mdi-delete',
    column_added: 'mdi-table-column-plus-after',
    column_updated: 'mdi-table-edit',
    column_deleted: 'mdi-table-column-remove',
    data_created: 'mdi-plus',
    data_updated: 'mdi-pencil',
    data_deleted: 'mdi-delete',
    permission_changed: 'mdi-shield-account',
    exported: 'mdi-download'
  };
  return icons[action] || 'mdi-information';
};

const getActionLabel = (action) => {
  const label = actionTypes.find(t => t.value === action);
  return label ? label.title : action;
};

const formatValue = (value) => {
  if (!value) return '';

  // Wenn es ein Objekt ist, versuche es benutzerfreundlich darzustellen
  if (typeof value === 'object') {
    try {
      const obj = typeof value === 'string' ? JSON.parse(value) : value;

      // Für Listen-Updates
      if (obj.name) {
        let changes = [];
        if (obj.name) changes.push(`Name: "${obj.name}"`);
        if (obj.description) changes.push(`Beschreibung: "${obj.description}"`);
        if (obj.allow_export !== undefined) changes.push(`Export: ${obj.allow_export ? 'Erlaubt' : 'Verboten'}`);
        if (obj.allow_import !== undefined) changes.push(`Import: ${obj.allow_import ? 'Erlaubt' : 'Verboten'}`);

        // Berechtigungen
        if (obj.permissions && Array.isArray(obj.permissions)) {
          const permList = obj.permissions.map(p => {
            const rights = [];
            if (p.can_view) rights.push('Ansehen');
            if (p.can_create) rights.push('Erstellen');
            if (p.can_edit) rights.push('Bearbeiten');
            if (p.can_delete) rights.push('Löschen');
            if (p.can_export) rights.push('Exportieren');
            if (p.can_manage) rights.push('Verwalten');

            const typeLabel = p.permission_type === 'group' ? 'Gruppe' :
                            p.permission_type === 'role' ? 'Rolle' :
                            p.permission_type === 'user' ? 'Benutzer' : 'Öffentlich';

            return `${typeLabel} "${p.display_name || p.entity_id}": ${rights.join(', ')}`;
          });
          changes.push(`Berechtigungen:\n  - ${permList.join('\n  - ')}`);
        }

        return changes.join('\n');
      }

      // Für Daten-Updates
      if (!obj.name && Object.keys(obj).length > 0) {
        const entries = Object.entries(obj).map(([key, val]) => {
          if (typeof val === 'object') {
            return `${key}: ${JSON.stringify(val)}`;
          }
          return `${key}: ${val}`;
        });
        return entries.join('\n');
      }

      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return JSON.stringify(value, null, 2);
    }
  }

  return value;
};

// Lifecycle
onMounted(() => {
  loadLogs();
  loadAvailableLists();
});
</script>

<style scoped>
.value-box {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  max-height: 200px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.old-value {
  border-left: 3px solid #f44336;
  background: rgba(244, 67, 54, 0.1);
}

.new-value {
  border-left: 3px solid #4caf50;
  background: rgba(76, 175, 80, 0.1);
}
</style>
